import { useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, X, Link as LinkIcon, MessageCircle, Copy, Landmark } from "lucide-react";

const inr = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const waNumber = (raw) => { const d = (raw || "").replace(/\D/g, ""); return !d ? "" : d.length === 10 ? `91${d}` : d; };
const copy = async (text, ok) => { try { await navigator.clipboard.writeText(text); toast.success(ok); } catch { toast.error("Copy not available here"); } };

// Ask the customer to pay: a payment link (UPI / card / netbanking) or a bank transfer to our account.
export default function CollectPaymentDialog({ invoice, onClose }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  if (!invoice) return null;
  const b = invoice.bank_details || {};
  const bankReady = b.account_number && b.account_number !== "TBD" && b.ifsc_code && b.ifsc_code !== "TBD";
  const due = Number(invoice.total_amount || 0) - Number(invoice.amount_paid || 0);
  const phone = waNumber(invoice.customer_phone || invoice.customer_mobile);
  const wa = (msg) => window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");

  const makeLink = async () => {
    setBusy(true);
    try {
      const r = await api.post("/v2/payments/links/create", { invoice_id: invoice.id, customer_id: invoice.customer_id, expiry_hours: 48 });
      const url = r.data?.link_url || r.data?.payment_link || r.data?.url || "";
      if (!url) throw new Error();
      setLink(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the payment link"); }
    finally { setBusy(false); }
  };
  const linkMsg = `Hi ${invoice.customer_name || ""}, your Sendtoo invoice ${invoice.invoice_number} for ₹${inr(due)} is ready. Pay securely here: ${link}`;
  const bankText = [`Sendtoo (Capexlite Logistics Solutions Pvt Ltd) - bank transfer details`, `Invoice: ${invoice.invoice_number}`, `Amount: ₹${inr(due)}`,
    `Bank: ${b.bank_name || ""}`, `A/c holder: ${b.account_holder || ""}`, `A/c number: ${b.account_number || ""}`, `IFSC: ${b.ifsc_code || ""}`,
    b.branch ? `Branch: ${b.branch}` : "", b.upi_id && b.upi_id !== "TBD" ? `UPI: ${b.upi_id}` : "",
    `Please quote ${invoice.invoice_number} as the payment reference and share the UTR once paid.`].filter(Boolean).join("\n");

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="collect-payment-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div><h2 className="text-lg font-bold text-slate-900">Collect payment</h2>
            <p className="text-xs text-slate-500">{invoice.invoice_number} · amount due ₹{inr(due)}</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <section>
            <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><LinkIcon className="w-4 h-4" /> Payment link</p>
            <p className="text-xs text-slate-500 mb-2">UPI, card or net banking. Valid for 48 hours for the amount shown above.</p>
            {!link ? <Button size="sm" onClick={makeLink} disabled={busy} data-testid="make-link">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create payment link"}</Button> : (
              <div className="space-y-2">
                <input readOnly className="w-full rounded-md border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs" value={link} onFocus={(e) => e.target.select()} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => copy(link, "Link copied")}><Copy className="w-4 h-4 mr-1" />Copy link</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => wa(linkMsg)}><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
                </div>
              </div>
            )}
          </section>
          <section className="border-t border-slate-100 pt-4">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Landmark className="w-4 h-4" /> Bank transfer</p>
            {!bankReady && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 my-2">Bank account details are not set up yet (they show as TBD). Add them in Admin settings before using this option.</p>}
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 my-2 whitespace-pre-wrap">{bankText}</pre>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!bankReady} onClick={() => copy(bankText, "Bank details copied")}><Copy className="w-4 h-4 mr-1" />Copy details</Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!bankReady} onClick={() => wa(bankText)}><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">When the money arrives, use "Mark Paid" on the invoice with the bank reference.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
